import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { CoverageRow } from './coverageTypes';
import { STATE_PRESENTATION } from './coveragePresentation';

/**
 * THE THREE ELEMENTS THE COVERAGE PANEL REPEATS.
 *
 * Extracted verbatim from `MissionCoveragePanel.tsx` — a state badge, one labelled control, and
 * one label/value row. Nothing here holds state or fetches anything.
 */

export function StateBadge({ state }: { state: CoverageRow['state'] }) {
  // `?? fallback`, because the state arrives from a SOCKET FRAME that this bundle does not
  // validate. A server that gains a seventh coverage state while an operator's tab holds the old
  // build would otherwise destructure undefined and take the whole mission page down — there is
  // no error boundary under (dashboard). The COP's own map already guards this way.
  const { label, className } = STATE_PRESENTATION[state]
    ?? { label: String(state).toUpperCase(), className: 'bg-muted text-muted-foreground' };
  return (
    <span
      // The live region above announces the same words, so the visible badge needs a hook a test
      // can aim at without also matching its own screen-reader copy.
      data-testid="coverage-state-badge"
      className={cn(
        'inline-flex items-center shrink-0 px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wider',
        className,
      )}
    >
      {label}
    </span>
  );
}

/** One labelled control. `htmlFor` is required — a sibling Radix Label associates nothing. */
export function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1 min-w-0">
      <Label htmlFor={id} className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function ReadRow({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="w-28 shrink-0 text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      <span className={cn(
        'min-w-0 flex-1 truncate text-xs font-mono tabular-nums',
        bad ? 'text-status-amber-fg' : 'text-foreground',
      )}>
        {value}
      </span>
    </div>
  );
}
